/**
 * A question's answer in the document is one block-level content control
 * tagged with the bid and the question's section id. Headings inside it are
 * formatting; text outside every block is never pushed. The control is hidden
 * (no border) except while its row is hovered in the Tender tab.
 *
 * Everything written here is a tracked change authored "mytender.io" where
 * Word allows it (OOXML with revision marks, inserted with tracking off), and
 * an ordinary tracked change otherwise.
 */
import { deleteParagraphAsAuthoredRevision } from "./authoredRevision";
import { describeError, markAuthoredRevisionsWorking } from "./wordTools";
import { type Block, type Inline, blocksToMarkdown, parseInline, parseMarkdown } from "./markdown";
import { type TemplateStyleMap, type TemplateStyleRef, loadAppliedTemplate } from "./identity";
import { blockControlXml, blocksXml, headingXml, packageOf } from "./revisionOoxml";
import { TEMPLATE_FRAME_TAG } from "./templateFrame";
import { type TableRead, readTables } from "./tables";
import { documentCapabilities, isOfficeDocumentAvailable } from "./snapshot";

const TAG_PREFIX = "mytender-q";

export const blockTag = (bidId: string, sectionId: string): string => `${TAG_PREFIX}:${bidId}:${sectionId}`;

const parseTag = (tag: string | null | undefined): { bidId: string; sectionId: string } | null => {
  const parts = (tag ?? "").split(":");
  return parts.length === 3 && parts[0] === TAG_PREFIX ? { bidId: parts[1], sectionId: parts[2] } : null;
};

export type BlockInfo = { sectionId: string; words: number };
export type BlocksState = { blocks: Map<string, BlockInfo>; otherBidId: string | null };

const wordCount = (text: string): number => (text.match(/\S+/g) ?? []).length;

/** Which questions have a block in this document, and whether blocks belong to another bid. */
export const listBlocks = async (bidId: string): Promise<BlocksState> => {
  if (!isOfficeDocumentAvailable()) return { blocks: new Map(), otherBidId: null };
  return Word.run(async (context) => {
    const controls = context.document.body.contentControls;
    controls.load("items/tag,items/text");
    await context.sync();
    const blocks = new Map<string, BlockInfo>();
    let otherBidId: string | null = null;
    for (const c of controls.items) {
      const parsed = parseTag(c.tag);
      if (!parsed) continue;
      if (parsed.bidId !== bidId) {
        otherBidId ??= parsed.bidId;
        continue;
      }
      const words = wordCount(c.text ?? "") + (blocks.get(parsed.sectionId)?.words ?? 0);
      blocks.set(parsed.sectionId, { sectionId: parsed.sectionId, words });
    }
    return { blocks, otherBidId };
  });
};

const controlFor = (context: Word.RequestContext, bidId: string, sectionId: string) =>
  context.document.body.contentControls.getByTag(blockTag(bidId, sectionId)).getFirstOrNullObject();

/** Each answer block's paragraph range (0-based body paragraph indexes). */
export const questionRanges = async (): Promise<{ section_id: string; start: number; end: number }[]> => {
  if (!isOfficeDocumentAvailable()) return [];
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items");
    await context.sync();
    const parents = paragraphs.items.map((p) => {
      const c = p.parentContentControlOrNullObject;
      c.load("isNullObject,tag");
      return c;
    });
    await context.sync();
    const byId = new Map<string, { section_id: string; start: number; end: number }>();
    parents.forEach((c, index) => {
      const parsed = c.isNullObject ? null : parseTag(c.tag);
      if (!parsed) return;
      const found = byId.get(parsed.sectionId);
      if (found) found.end = index;
      else byId.set(parsed.sectionId, { section_id: parsed.sectionId, start: index, end: index });
    });
    return [...byId.values()];
  });
};

/** Outline a question's block while its row is hovered (writes nothing to the text). */
export const highlightBlock = async (bidId: string, sectionId: string, on: boolean): Promise<void> => {
  if (!isOfficeDocumentAvailable()) return;
  await Word.run(async (context) => {
    const control = controlFor(context, bidId, sectionId);
    control.load("isNullObject");
    await context.sync();
    if (control.isNullObject) return;
    control.appearance = on ? "BoundingBox" : "Hidden";
    await context.sync();
  }).catch(() => undefined);
};

/**
 * Scroll to a question's answer, select it and draw its box (the block's
 * outline, as hovering its row does). The caller hides the box again.
 */
export const showBlock = async (bidId: string, sectionId: string): Promise<boolean> => {
  if (!isOfficeDocumentAvailable()) return false;
  return Word.run(async (context) => {
    const control = controlFor(context, bidId, sectionId);
    control.load("isNullObject");
    await context.sync();
    if (control.isNullObject) return false;
    control.appearance = "BoundingBox";
    // The cursor only, so a stray keypress can't replace the answer.
    control.getRange("Start").select();
    await context.sync();
    return true;
  });
};

// --------------------------------------------------------------------------- //
// Reading an answer as markdown
// --------------------------------------------------------------------------- //
const HEADING = /^heading\s*([1-6])$/i;

/** Inline formatting from a paragraph's HTML; deleted revisions are dropped. */
const inlinesFromHtml = (html: string): Inline[] => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const out: Inline[] = [];
  const walk = (node: Node, bold: boolean, italic: boolean, href?: string) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent ?? "").replace(/\s+/g, " ");
      if (text) out.push({ text, ...(bold && { bold }), ...(italic && { italic }), ...(href && { href }) });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName;
    if (tag === "DEL" || tag === "S" || tag === "STYLE" || tag === "SCRIPT") return;
    const style = (el.getAttribute("style") ?? "").toLowerCase();
    const b = bold || tag === "B" || tag === "STRONG" || /font-weight:\s*(bold|[6-9]00)/.test(style);
    const i = italic || tag === "I" || tag === "EM" || /font-style:\s*italic/.test(style);
    const link =
      tag === "A" && /^(https?:|mailto:)/i.test(el.getAttribute("href") ?? "")
        ? (el.getAttribute("href") ?? undefined)
        : href;
    el.childNodes.forEach((child) => walk(child, b, i, link));
  };
  doc.body.childNodes.forEach((n) => walk(n, false, false));
  // Merge neighbours with the same formatting.
  const merged: Inline[] = [];
  for (const i of out) {
    const prev = merged[merged.length - 1];
    if (prev && !!prev.bold === !!i.bold && !!prev.italic === !!i.italic && prev.href === i.href) prev.text += i.text;
    else merged.push({ ...i });
  }
  return merged.map((i) => ({ ...i, text: i.text })).filter((i) => i.text.trim() || i.text === " ");
};

const trimInlines = (inlines: Inline[]): Inline[] => {
  const copy = inlines.map((i) => ({ ...i }));
  if (copy.length) copy[0].text = copy[0].text.replace(/^\s+/, "");
  if (copy.length) copy[copy.length - 1].text = copy[copy.length - 1].text.replace(/\s+$/, "");
  return copy.filter((i) => i.text);
};

/** A typed list marker at the start of a paragraph: "•\t" (bullet) or "3.\t" (number, group 1). */
const TYPED_MARKER = /^(?:[•◦▪‣∙·-]|(\d+)[.)])\t/;
/** Indent per list level in points, as revisionOoxml writes it (360 twips). */
const INDENT_POINTS = 18;

/** Inlines without a leading marker that may span the first inlines. */
const stripMarker = (inlines: Inline[], marker: string): Inline[] => {
  let left = marker.length;
  const out: Inline[] = [];
  for (const i of inlines) {
    if (left <= 0) {
      out.push(i);
      continue;
    }
    const text = i.text.replace(/^\s+/, "");
    if (text.length <= left) {
      left -= text.length;
      continue;
    }
    out.push({ ...i, text: text.slice(left) });
    left = 0;
  }
  return trimInlines(out);
};

type ParagraphRead = {
  paragraph: Word.Paragraph;
  current: OfficeExtension.ClientResult<string> | null;
  original: OfficeExtension.ClientResult<string> | null;
  /** Null for a paragraph with no formatting or links: its text is enough. */
  html: OfficeExtension.ClientResult<string> | null;
  /** The paragraph's range, loaded with `hyperlink`, for plain-looking paragraphs. */
  range: Word.Range | null;
  list: Word.ListItem;
};

/**
 * A range's paragraphs as markdown blocks, with tracked changes counted as
 * accepted. Tables come from their cell values; paragraphs inside tables are
 * skipped so each table appears once.
 */
const readRangeBlocks = async (
  context: Word.RequestContext,
  range: Word.Range | Word.ContentControl
): Promise<{ blocks: Block[]; pendingChanges: number }> => {
  const caps = documentCapabilities();
  const paragraphs = range.paragraphs;
  paragraphs.load(
    "items/text,items/style,items/styleBuiltIn,items/isListItem,items/tableNestingLevel,items/leftIndent,items/font/bold,items/font/italic"
  );
  // Reviewed text is only needed where revisions exist; one count for the
  // whole range says whether any do.
  const revisions = caps.review ? range.getTrackedChanges() : null;
  revisions?.load("items");
  await context.sync();
  const hasRevisions = revisions ? revisions.items.length > 0 : caps.tracked_changes;
  const tables = await readTables(context, range.tables);

  // getHtml is the slow call (it renders the paragraph): only paragraphs
  // with bold, italic (or mixed: null) or a link need it; plain ones use
  // their text.
  const readParagraph = (p: Word.Paragraph): ParagraphRead => {
    const list = p.listItemOrNullObject;
    list.load("level,listString");
    const plainFont = p.font.bold === false && p.font.italic === false;
    const r = p.getRange();
    if (plainFont) r.load("hyperlink");
    return {
      paragraph: p,
      current: hasRevisions ? r.getReviewedText("Current") : null,
      original: hasRevisions ? r.getReviewedText("Original") : null,
      html: plainFont ? null : p.getHtml(),
      range: plainFont ? r : null,
      list,
    };
  };
  let reads: ParagraphRead[] = paragraphs.items.map(readParagraph);
  try {
    await context.sync();
  } catch (error) {
    // One paragraph Word can't render or review fails the whole batch:
    // read them one by one, and fall back to plain text for the culprit.
    console.warn("Could not read every paragraph at once; reading them one by one", describeError(error));
    reads = [];
    for (const p of paragraphs.items) {
      const read = readParagraph(p);
      try {
        await context.sync();
        reads.push(read);
      } catch (one) {
        console.warn("Word could not read a paragraph; using its plain text", describeError(one));
        const list = p.listItemOrNullObject;
        list.load("level,listString");
        await context.sync().catch(() => undefined);
        reads.push({ paragraph: p, current: null, original: null, html: null, range: null, list });
      }
    }
  }
  // A plain-looking paragraph with a link needs its HTML after all.
  const linked = reads.filter((r) => !r.html && r.range?.hyperlink);
  for (const r of linked) r.html = r.paragraph.getHtml();
  if (linked.length) await context.sync();

  const blocks: Block[] = [];
  let pendingChanges = 0;
  let tableIndex = 0;
  let inTable = false;
  for (const r of reads) {
    const p = r.paragraph;
    if ((p.tableNestingLevel ?? 0) > 0) {
      if (!inTable) {
        const table = tables[tableIndex++];
        if (table?.deleted) {
          // A table deleted as a tracked change: gone once accepted.
          pendingChanges += 1;
        } else if (table?.values) {
          blocks.push({
            kind: "table",
            header: table.headerRowCount > 0 || table.values.length > 1,
            rows: table.values.map((row) => row.map((cell) => parseInline(String(cell ?? "")))),
          });
        } else if (table?.text) {
          // Word couldn't report the cells; keep what remains as paragraphs.
          // eslint-disable-next-line no-control-regex -- Word's manual line break is U+000B
          for (const line of table.text.split(/[\r\n\u000B]+/)) {
            if (line.trim()) blocks.push({ kind: "paragraph", inlines: [{ text: line.trim() }] });
          }
        }
        inTable = true;
      }
      continue;
    }
    inTable = false;
    const current = r.current?.value ?? p.text ?? "";
    const changed = r.original !== null && r.current !== null && r.original.value !== r.current.value;
    if (changed) pendingChanges += 1;
    if (!current.trim()) continue;
    const inlines = trimInlines(
      changed || !r.html ? [{ text: current }] : inlinesFromHtml(r.html.value || "") || [{ text: current }]
    );
    const safe = inlines.length ? inlines : [{ text: current.trim() }];
    const heading = HEADING.exec(String(p.styleBuiltIn ?? "")) ?? HEADING.exec(p.style ?? "");
    // Lists pulled from mytender are paragraphs with a typed "•\t" or "1.\t"
    // marker (revisionOoxml.ts), not Word lists; read them back as lists.
    const typed = heading || p.isListItem ? null : TYPED_MARKER.exec(current.replace(/^\s+/, ""));
    if (typed) {
      const depth = Math.max(0, Math.round((p.leftIndent ?? 0) / INDENT_POINTS) - 1);
      const rest = stripMarker(safe, typed[0]);
      const body = rest.length ? rest : [{ text: current.replace(TYPED_MARKER, "").trim() }];
      blocks.push(
        typed[1] ? { kind: "number", depth, ordinal: 1, inlines: body } : { kind: "bullet", depth, inlines: body }
      );
      continue;
    }
    if (heading) {
      blocks.push({ kind: "heading", level: Number(heading[1]), inlines: safe });
    } else if (p.isListItem && !r.list.isNullObject) {
      const numbered = /\d/.test(r.list.listString ?? "");
      const depth = r.list.level ?? 0;
      blocks.push(
        numbered ? { kind: "number", depth, ordinal: 1, inlines: safe } : { kind: "bullet", depth, inlines: safe }
      );
    } else {
      blocks.push({ kind: "paragraph", inlines: safe });
    }
  }
  // Renumber numbered runs so the markdown reads 1., 2., 3.
  let ordinal = 0;
  let prevNumbered = false;
  for (const b of blocks) {
    if (b.kind === "number") {
      ordinal = prevNumbered ? ordinal + 1 : 1;
      b.ordinal = ordinal;
      prevNumbered = true;
    } else prevNumbered = false;
  }
  return { blocks, pendingChanges };
};

export type BlockContent = { sectionId: string; markdown: string; pendingChanges: number; figures: number };

/** Every question block's answer as markdown, tracked changes as accepted. */
export const readBlocks = async (bidId: string, sectionIds?: string[]): Promise<Map<string, BlockContent>> => {
  const out = new Map<string, BlockContent>();
  if (!isOfficeDocumentAvailable()) return out;
  return Word.run(async (context) => {
    const controls = context.document.body.contentControls;
    controls.load("items/tag");
    await context.sync();
    for (const c of controls.items) {
      const parsed = parseTag(c.tag);
      if (!parsed || parsed.bidId !== bidId) continue;
      if (sectionIds && !sectionIds.includes(parsed.sectionId)) continue;
      const pictures = c.inlinePictures;
      pictures.load("items");
      const { blocks, pendingChanges } = await readRangeBlocks(context, c);
      await context.sync();
      const figures = pictures.items.length;
      const markdown =
        blocksToMarkdown(blocks) +
        (figures ? `\n\n${Array.from({ length: figures }, () => "[figure]").join("\n\n")}` : "");
      // A block copied and pasted in Word keeps its tag, so one question can
      // have two blocks: push both, in document order, rather than lose one.
      const prev = out.get(parsed.sectionId);
      out.set(
        parsed.sectionId,
        prev
          ? {
              sectionId: parsed.sectionId,
              markdown: [prev.markdown, markdown.trim()].filter(Boolean).join("\n\n"),
              pendingChanges: prev.pendingChanges + pendingChanges,
              figures: prev.figures + figures,
            }
          : { sectionId: parsed.sectionId, markdown: markdown.trim(), pendingChanges, figures }
      );
    }
    return out;
  });
};

// --------------------------------------------------------------------------- //
// Writing: pull and assign
// --------------------------------------------------------------------------- //
export type QuestionRef = { sectionId: string; number: string; heading: string; level: 1 | 2 | 3 };

const titleOf = (q: QuestionRef) => [q.number, q.heading].filter(Boolean).join(" · ") || "Question";

/** Markdown → HTML, for the ordinary-tracked-change fallback. */
const blocksHtml = (blocks: Block[]): string => {
  const e = (t: string) => t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inl = (xs: Inline[]) =>
    xs
      .map((i) => {
        const t = i.bold
          ? `<b>${i.italic ? `<i>${e(i.text)}</i>` : e(i.text)}</b>`
          : i.italic
            ? `<i>${e(i.text)}</i>`
            : e(i.text);
        return i.href ? `<a href="${e(i.href).replace(/"/g, "&quot;")}">${t}</a>` : t;
      })
      .join("");
  const out: string[] = [];
  let list: "ul" | "ol" | null = null;
  const close = () => {
    if (list) out.push(`</${list}>`);
    list = null;
  };
  for (const b of blocks) {
    if (b.kind === "bullet" || b.kind === "number") {
      const want = b.kind === "bullet" ? "ul" : "ol";
      if (list !== want) {
        close();
        out.push(`<${want}>`);
        list = want;
      }
      out.push(`<li>${inl(b.inlines)}</li>`);
      continue;
    }
    close();
    if (b.kind === "heading") out.push(`<h${b.level}>${inl(b.inlines)}</h${b.level}>`);
    else if (b.kind === "paragraph") out.push(`<p>${inl(b.inlines)}</p>`);
    else if (b.kind === "table")
      out.push(
        `<table border="1" style="border-collapse:collapse">${b.rows
          .map(
            (r, i) =>
              `<tr>${r.map((c) => (i === 0 && b.header ? `<th>${inl(c)}</th>` : `<td>${inl(c)}</td>`)).join("")}</tr>`
          )
          .join("")}</table>`
      );
  }
  close();
  return out.join("") || "<p></p>";
};

const setTracking = async (
  context: Word.RequestContext,
  mode: Word.ChangeTrackingMode | `${Word.ChangeTrackingMode}`
) => {
  const document = context.document;
  document.load("changeTrackingMode");
  await context.sync();
  const previous = document.changeTrackingMode;
  if (previous !== mode) {
    document.changeTrackingMode = mode as Word.ChangeTrackingMode;
    await context.sync();
  }
  return async () => {
    if (previous !== mode) {
      document.changeTrackingMode = previous;
      await context.sync();
    }
  };
};

/** A block's current paragraphs and tables, read before anything is written into it. */
/**
 * A block's content before a write. `texts` is what each paragraph reads as
 * with pending revisions applied (Word's `text` shows deleted text too), so
 * a diff against a block that still holds suggestions compares what the
 * user sees.
 */
type OldContent = {
  paragraphs: Word.Paragraph[];
  /** The readable tables, in order, each with what Word reported of it. */
  tables: { t: Word.Table; read: TableRead }[];
  texts: string[];
};

/** The paragraphs' current text: reviewed where the block holds revisions, plain otherwise. */
const currentTexts = async (
  context: Word.RequestContext,
  control: Word.ContentControl,
  paragraphs: Word.Paragraph[]
): Promise<string[]> => {
  if (!documentCapabilities().review) return paragraphs.map((p) => p.text ?? "");
  const revisions = control.getTrackedChanges();
  revisions.load("items");
  await context.sync();
  if (!revisions.items.length) return paragraphs.map((p) => p.text ?? "");
  const reads = paragraphs.map((p) => p.getRange("Whole").getReviewedText("Current"));
  await context.sync();
  return reads.map((r, i) => r.value ?? paragraphs[i].text ?? "");
};

const readOldContent = async (context: Word.RequestContext, control: Word.ContentControl): Promise<OldContent> => {
  const paragraphs = control.paragraphs;
  paragraphs.load("items/text,items/style,items/tableNestingLevel");
  await context.sync();
  const reads = await readTables(context, control.tables);
  return {
    paragraphs: paragraphs.items,
    // A table already deleted as a tracked change is not content to diff against.
    tables: control.tables.items.map((t, i) => ({ t, read: reads[i] })).filter((x) => x.read && !x.read.deleted),
    texts: await currentTexts(context, control, paragraphs.items),
  };
};

// --------------------------------------------------------------------------- //
// Replacing an answer as a paragraph-level diff
// --------------------------------------------------------------------------- //
type OldUnit = { kind: "paragraph"; p: Word.Paragraph; key: string } | { kind: "table"; t: Word.Table; key: string };

/**
 * Text as it reads for matching: whitespace collapsed, and a leading list
 * marker dropped so a typed "•\t" marker, a real Word list and a markdown
 * "- " all read the same.
 */
const normKey = (text: string): string =>
  text
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^(?:[•◦▪‣∙·\-*]|\d+[.)])\s+/, "");
const inlineText = (inlines: Inline[]): string => inlines.map((i) => i.text).join("");

/** What a block reads as, for matching against the document's paragraphs. */
export const blockKey = (b: Block): string => {
  switch (b.kind) {
    case "table":
      return `table:${JSON.stringify(b.rows.map((r) => r.map((c) => normKey(inlineText(c)))))}`;
    case "bullet":
      return normKey(`• ${inlineText(b.inlines)}`);
    case "number":
      return normKey(`${b.ordinal}. ${inlineText(b.inlines)}`);
    case "pagebreak":
      return "pagebreak";
    default:
      return normKey(inlineText(b.inlines));
  }
};

/** The block's paragraphs and tables as one sequence, in document order. */
const oldUnits = (old: OldContent): OldUnit[] => {
  const units: OldUnit[] = [];
  let tableIndex = 0;
  let inTable = false;
  old.paragraphs.forEach((p, index) => {
    if ((p.tableNestingLevel ?? 0) > 0) {
      if (!inTable) {
        const entry = old.tables[tableIndex++];
        if (entry)
          units.push({
            kind: "table",
            t: entry.t,
            key: entry.read.values
              ? `table:${JSON.stringify(entry.read.values.map((r) => r.map((c) => normKey(String(c ?? "")))))}`
              : `table-text:${normKey(entry.read.text)}`,
          });
        inTable = true;
      }
      return;
    }
    inTable = false;
    units.push({ kind: "paragraph", p, key: normKey(old.texts[index] ?? p.text ?? "") });
  });
  return units;
};

/** Longest common subsequence: pairs of matching indexes, in order. Empty keys never match. */
export const lcsMatches = (a: string[], b: string[]): [number, number][] => {
  const n = a.length;
  const m = b.length;
  const eq = (i: number, j: number) => a[i] !== "" && a[i] === b[j];
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1)
    for (let j = m - 1; j >= 0; j -= 1)
      dp[i][j] = eq(i, j) ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out: [number, number][] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (eq(i, j)) {
      out.push([i, j]);
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) i += 1;
    else j += 1;
  }
  return out;
};

/** Whether a diff would change anything: unmatched old units (empty lines aside) or new blocks. */
const diffChanges = (old: OldContent, blocks: Block[]): boolean => {
  const A = oldUnits(old);
  const B = blocks.map(blockKey);
  const matches = lcsMatches(
    A.map((u) => u.key),
    B
  );
  const matchedA = new Set(matches.map((m) => m[0]));
  return A.some((u, i) => !matchedA.has(i) && u.key !== "") || matches.length < B.length;
};

/**
 * Queue the revisions that turn a block's paragraphs into `blocks`:
 * paragraphs and tables that read the same are left alone, new ones go in
 * (as mytender.io insertions) before the next unchanged one or after the
 * last, and the rest are marked deleted. Word's margin then shows one change
 * per changed paragraph. Formatting-only differences read as unchanged.
 * Nothing is synced here; the caller syncs, deletes the returned tables as
 * ordinary tracked changes, and rewraps the block `delta` paragraphs longer.
 */
const queueDiff = (control: Word.ContentControl, old: OldContent, blocks: Block[]): { oldTables: Word.Table[] } => {
  const A = oldUnits(old);
  const B = blocks.map((block) => ({ block, key: blockKey(block) }));
  const matches = lcsMatches(
    A.map((u) => u.key),
    B.map((u) => u.key)
  );
  const matchedA = new Set(matches.map((m) => m[0]));
  const rangeOf = (u: OldUnit) => (u.kind === "paragraph" ? u.p.getRange("Whole") : u.t.getRange("Whole"));

  // New blocks go in runs, each before the next unchanged unit (through the
  // block's start when that is its first unit), or at the block's end.
  const inserts: { before: OldUnit | null; atStart: boolean; blocks: Block[] }[] = [];
  let next = 0;
  for (const [ai, bi] of matches) {
    if (bi > next) inserts.push({ before: A[ai], atStart: ai === 0, blocks: B.slice(next, bi).map((x) => x.block) });
    next = bi + 1;
  }
  if (next < B.length) inserts.push({ before: null, atStart: false, blocks: B.slice(next).map((x) => x.block) });
  for (const ins of inserts) {
    const xml = packageOf(blocksXml(ins.blocks, { tracked: true }));
    if (!ins.before) control.insertOoxml(xml, "End");
    else if (ins.atStart) control.insertOoxml(xml, "Start");
    else rangeOf(ins.before).insertOoxml(xml, "Before");
  }

  const oldTables: Word.Table[] = [];
  const textOf = new Map(old.paragraphs.map((p, i) => [p, old.texts[i] ?? p.text ?? ""]));
  A.forEach((u, i) => {
    if (matchedA.has(i)) return;
    if (u.kind === "table") {
      oldTables.push(u.t);
      return;
    }
    const text = textOf.get(u.p) ?? "";
    if (!text.trim()) {
      u.p.delete();
      return;
    }
    deleteParagraphAsAuthoredRevision(u.p, text, u.p.style ?? null);
  });
  return { oldTables };
};

/**
 * Replace one block's answer as a diff (see {@link queueDiff}).
 */
const replaceInControl = async (
  context: Word.RequestContext,
  control: Word.ContentControl,
  blocks: Block[]
): Promise<"replaced" | "unchanged"> => {
  const old = await readOldContent(context, control);
  if (!diffChanges(old, blocks)) return "unchanged";
  const diff = queueDiff(control, old, blocks);
  await context.sync();
  if (diff.oldTables.length) {
    // Tables can't carry authored deletions reliably; ordinary tracked ones.
    const restoreTracked = await setTracking(context, Word.ChangeTrackingMode.trackAll);
    for (const t of diff.oldTables) t.delete();
    await context.sync();
    await restoreTracked();
  }
  return "replaced";
};

/** Whether a question's block is still in the document. */
const blockExists = async (context: Word.RequestContext, bidId: string, sectionId: string): Promise<boolean> => {
  const check = controlFor(context, bidId, sectionId);
  check.load("isNullObject");
  await context.sync();
  return !check.isNullObject;
};

export type PullOutcome = {
  sectionId: string;
  status: "replaced" | "added" | "unchanged" | "error";
  message?: string;
};

/** Questions around one in mytender's order, nearest first, for placing a new block. */
export type Neighbours = { before: string[]; after: string[] };

/**
 * Where a new question's heading and block go: straight after the nearest
 * earlier question's block; failing that, before the nearest later question
 * (above its heading when one sits right before its block); otherwise at the
 * end of the document.
 */
type Anchor =
  | { kind: "after"; control: Word.ContentControl }
  | { kind: "before"; control: Word.ContentControl }
  | { kind: "beforeParagraph"; paragraph: Word.Paragraph }
  /** Straight after a paragraph: a heading the template already has for this answer. */
  | { kind: "afterParagraph"; paragraph: Word.Paragraph }
  | { kind: "start" }
  | { kind: "end" };

const isHeadingStyle = (p: Word.Paragraph): boolean =>
  /^heading/i.test(String(p.styleBuiltIn ?? "")) || /^(heading|title)/i.test(p.style ?? "");

/** A title with numbering, punctuation and case taken out, for matching a heading to a question. */
const titleKey = (text: string): string =>
  text
    .toLowerCase()
    .replace(/^[\s(]*(q(uestion)?\s*)?[\d.]+[a-z]?[).:\s-]+/i, "")
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Whether a heading in the document is this question's own heading. */
const headingMatches = (headingText: string, title: string): boolean => {
  const a = titleKey(headingText);
  const b = titleKey(title);
  if (!a || !b) return false;
  if (a === b) return true;
  const shorter = a.length <= b.length ? a : b;
  const longer = shorter === a ? b : a;
  return shorter.length >= 12 && longer.includes(shorter);
};

/**
 * The document's lay of the land for placing answers that have no neighbour
 * yet: its headings (outside answer blocks), the template's "response
 * starts here" heading if it has one, and the closing pages' control if the
 * document ends in one, so an answer never lands after the closing page.
 */
type Landmarks = {
  headings: { paragraph: Word.Paragraph; text: string }[];
  responseStart: Word.Paragraph | null;
  backFrame: Word.ContentControl | null;
  /** The control holding the cover/contents pages, when the body starts with one. */
  frontFrame: Word.ContentControl | null;
  /** Every body paragraph, by index, for the agent's after_paragraph / under_heading placements. */
  paragraphs: Word.Paragraph[];
};

const loadLandmarks = async (context: Word.RequestContext, responseStartText: string | null): Promise<Landmarks> => {
  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("items/text,items/style,items/styleBuiltIn,items/tableNestingLevel");
  await context.sync();
  // The frame controls sit around the first and last paragraphs with text
  // (Word keeps an empty paragraph after a control that ends the body).
  const withText = paragraphs.items.filter((p) => (p.text ?? "").trim());
  const first = withText[0] ?? null;
  const last = withText[withText.length - 1] ?? null;
  const firstControl = first ? first.parentContentControlOrNullObject : null;
  const lastControl = last ? last.parentContentControlOrNullObject : null;
  firstControl?.load("isNullObject,tag");
  lastControl?.load("isNullObject,tag");
  if (firstControl || lastControl) await context.sync();
  const candidates = paragraphs.items.filter(
    (p) => (p.tableNestingLevel ?? 0) === 0 && isHeadingStyle(p) && (p.text ?? "").trim()
  );
  // Headings inside an answer block belong to that answer, not to the outline.
  const owners = candidates.map((p) => {
    const c = p.parentContentControlOrNullObject;
    c.load("isNullObject,tag");
    return c;
  });
  await context.sync();
  const headings = candidates
    .filter((_, i) => owners[i].isNullObject || !parseTag(owners[i].tag))
    .map((p) => ({ paragraph: p, text: (p.text ?? "").trim() }));
  const wanted = responseStartText ? titleKey(responseStartText) : "";
  const responseStart = wanted ? (headings.find((h) => titleKey(h.text) === wanted)?.paragraph ?? null) : null;
  const backFrame =
    lastControl && !lastControl.isNullObject && lastControl.tag === TEMPLATE_FRAME_TAG ? lastControl : null;
  const frontFrame =
    firstControl && !firstControl.isNullObject && firstControl.tag === TEMPLATE_FRAME_TAG ? firstControl : null;
  return { headings, responseStart, backFrame, frontFrame, paragraphs: paragraphs.items };
};

/**
 * The anchor the agent asked for, or null when its index no longer holds
 * the paragraph it named (then the add-in's own choice applies instead of
 * a misplaced answer).
 */
const placementAnchor = (
  landmarks: Landmarks,
  placement: AnswerPlacement
): { anchor: Anchor; key: string; omitHeading: boolean } | null => {
  const omit =
    placement.include_heading === undefined ? placement.position === "under_heading" : !placement.include_heading;
  switch (placement.position) {
    case "under_heading":
    case "after_paragraph": {
      const paragraph = placement.index === undefined ? undefined : landmarks.paragraphs[placement.index];
      if (!paragraph) return null;
      const expected = (placement.expected_text ?? "").trim().toLowerCase();
      const actual = (paragraph.text ?? "").trim().toLowerCase();
      if (expected && !actual.startsWith(expected.slice(0, 40)) && !expected.startsWith(actual.slice(0, 40)))
        return null;
      return { anchor: { kind: "afterParagraph", paragraph }, key: `paragraph:${placement.index}`, omitHeading: omit };
    }
    case "start":
      return landmarks.frontFrame
        ? { anchor: { kind: "after", control: landmarks.frontFrame }, key: "front", omitHeading: omit }
        : { anchor: { kind: "start" }, key: "start-of-body", omitHeading: omit };
    case "before_closing_pages":
      return landmarks.backFrame
        ? { anchor: { kind: "before", control: landmarks.backFrame }, key: "frame", omitHeading: omit }
        : { anchor: { kind: "end" }, key: "end", omitHeading: omit };
    case "end":
      return { anchor: { kind: "end" }, key: "end", omitHeading: omit };
    default:
      return null;
  }
};

/** Where an answer with no neighbour goes: under its own heading, after the response-start heading, before the closing pages, or at the end. */
const landmarkAnchor = (landmarks: Landmarks, title: string): { anchor: Anchor; key: string; omitHeading: boolean } => {
  const own = landmarks.headings.find((h) => headingMatches(h.text, title));
  if (own)
    return {
      anchor: { kind: "afterParagraph", paragraph: own.paragraph },
      key: `heading:${own.text}`,
      omitHeading: true,
    };
  if (landmarks.responseStart)
    return { anchor: { kind: "afterParagraph", paragraph: landmarks.responseStart }, key: "start", omitHeading: false };
  if (landmarks.backFrame)
    return { anchor: { kind: "before", control: landmarks.backFrame }, key: "frame", omitHeading: false };
  return { anchor: { kind: "end" }, key: "end", omitHeading: false };
};

const anchorFor = async (
  context: Word.RequestContext,
  bidId: string,
  neighbours: Neighbours | undefined,
  title: string,
  responseStartText: string | null,
  placement?: AnswerPlacement
): Promise<{ anchor: Anchor; omitHeading: boolean }> => {
  const fallback = async () => {
    const landmarks = await loadLandmarks(context, responseStartText);
    const asked = placement && placement.position !== "auto" ? placementAnchor(landmarks, placement) : null;
    const { anchor, omitHeading } = asked ?? landmarkAnchor(landmarks, title);
    return { anchor, omitHeading };
  };
  if (placement && placement.position !== "auto") return fallback();
  if (!neighbours) return fallback();
  const load = (ids: string[]) =>
    ids.map((id) => {
      const c = controlFor(context, bidId, id);
      c.load("isNullObject");
      return c;
    });
  const before = load(neighbours.before);
  const after = load(neighbours.after);
  await context.sync();
  const prev = before.find((c) => !c.isNullObject);
  if (prev) return { anchor: { kind: "after", control: prev }, omitHeading: false };
  const next = after.find((c) => !c.isNullObject);
  if (!next) return fallback();
  const above = next.paragraphs.getFirst().getPreviousOrNullObject();
  above.load("isNullObject,style,styleBuiltIn");
  await context.sync();
  return {
    anchor:
      !above.isNullObject && isHeadingStyle(above)
        ? { kind: "beforeParagraph", paragraph: above }
        : { kind: "before", control: next },
    omitHeading: false,
  };
};

const insertXmlAt = (context: Word.RequestContext, anchor: Anchor, xml: string): void => {
  const pkg = packageOf(xml);
  if (anchor.kind === "after") anchor.control.getRange("Whole").insertOoxml(pkg, "After");
  else if (anchor.kind === "before") anchor.control.getRange("Whole").insertOoxml(pkg, "Before");
  else if (anchor.kind === "beforeParagraph") anchor.paragraph.getRange("Whole").insertOoxml(pkg, "Before");
  else if (anchor.kind === "afterParagraph") anchor.paragraph.getRange("Whole").insertOoxml(pkg, "After");
  else if (anchor.kind === "start") context.document.body.insertOoxml(pkg, "Start");
  else context.document.body.insertOoxml(pkg, "End");
};

const insertHeadingParagraphAt = (context: Word.RequestContext, anchor: Anchor, text: string): Word.Paragraph => {
  if (anchor.kind === "after") return anchor.control.insertParagraph(text, "After");
  if (anchor.kind === "before") return anchor.control.insertParagraph(text, "Before");
  if (anchor.kind === "beforeParagraph") return anchor.paragraph.insertParagraph(text, "Before");
  if (anchor.kind === "afterParagraph") return anchor.paragraph.insertParagraph(text, "After");
  if (anchor.kind === "start") return context.document.body.insertParagraph(text, "Start");
  return context.document.body.insertParagraph(text, "End");
};

/**
 * How an answer lands. "direct": plain content where there is nothing yet
 * (a pull into an empty document or an empty answer space lays out
 * mytender's content; there is nothing to review) but tracked wherever text
 * is replaced. "tracked": always as mytender.io revisions (io.ai's drafts,
 * which the writer reviews).
 */
export type PullMode = "direct" | "tracked";

/**
 * Write mytender's answer for a question into the document: replace its block
 * (as a diff, see {@link replaceInControl}), or add heading + block after the
 * previous question's answer (see {@link anchorFor}) when the document has
 * none.
 */
export const pullIntoDocument = async (
  bidId: string,
  q: QuestionRef,
  markdown: string,
  neighbours?: Neighbours,
  mode: PullMode = "tracked",
  directives: { placement?: AnswerPlacement; styles?: TemplateStyleMap | null } = {}
): Promise<PullOutcome> => {
  const blocks = parseMarkdown(markdown);
  const styles = directives.styles;
  const tag = blockTag(bidId, q.sectionId);
  const canTrack = documentCapabilities().tracked_changes;
  try {
    return await Word.run(async (context) => {
      const control = controlFor(context, bidId, q.sectionId);
      control.load("isNullObject");
      await context.sync();

      if (!control.isNullObject) {
        const old = await readOldContent(context, control);
        const hasText =
          old.tables.length > 0 ||
          old.paragraphs.some((p) => (p.tableNestingLevel ?? 0) === 0 && (p.text ?? "").trim());
        // Replacing text is always a change to review; filling an empty
        // answer space is content.
        const tracked = mode === "tracked" || hasText;
        if (!blocks.length && !hasText) return { sectionId: q.sectionId, status: "unchanged" as const };
        const restore = canTrack ? await setTracking(context, Word.ChangeTrackingMode.off) : async () => undefined;
        try {
          if (tracked) {
            const result = await replaceInControl(context, control, blocks);
            await restore();
            if (result === "replaced") markAuthoredRevisionsWorking();
            return (await blockExists(context, bidId, q.sectionId))
              ? { sectionId: q.sectionId, status: result }
              : { sectionId: q.sectionId, status: "error" as const, message: "Word removed the answer's block" };
          }
          control.insertOoxml(packageOf(blocksXml(blocks, { tracked: false, styles })), "End");
          await context.sync();
          for (const p of old.paragraphs) if (!(p.text ?? "").trim()) p.delete();
          await context.sync();
        } catch (authoredError) {
          await restore();
          // A change to review is a mytender.io revision or nothing: never
          // written under the user's name instead.
          if (tracked)
            return {
              sectionId: q.sectionId,
              status: "error" as const,
              message: `Word refused the change as a mytender.io revision (${describeError(authoredError)})`,
            };
          // Plain content (nothing to review) may take the HTML route.
          control.insertHtml(blocksHtml(blocks), "Replace");
          await context.sync();
          return (await blockExists(context, bidId, q.sectionId))
            ? { sectionId: q.sectionId, status: "replaced" as const }
            : { sectionId: q.sectionId, status: "error" as const, message: "Word removed the answer's block" };
        }
        await restore();
        return (await blockExists(context, bidId, q.sectionId))
          ? { sectionId: q.sectionId, status: "replaced" as const }
          : { sectionId: q.sectionId, status: "error" as const, message: "Word removed the answer's block" };
      }

      const applied = await loadAppliedTemplate().catch(() => null);
      const { anchor, omitHeading } = await anchorFor(
        context,
        bidId,
        neighbours,
        titleOf(q),
        applied?.styleMap?.response_start ?? null,
        directives.placement
      );
      const tracked = mode === "tracked";
      const restore = canTrack ? await setTracking(context, Word.ChangeTrackingMode.off) : async () => undefined;
      try {
        const xml =
          (omitHeading ? "" : headingXml(titleOf(q).replace(" · ", " "), q.level, { tracked, styles })) +
          blockControlXml(blocksXml(blocks, { tracked, styles }), tag, titleOf(q));
        insertXmlAt(context, anchor, xml);
        await context.sync();
        const check = controlFor(context, bidId, q.sectionId);
        check.load("isNullObject");
        await context.sync();
        if (check.isNullObject) throw new Error("block not created");
        await restore();
        if (tracked) markAuthoredRevisionsWorking();
        return { sectionId: q.sectionId, status: "added" as const };
      } catch (authoredError) {
        await restore();
        if (tracked)
          return {
            sectionId: q.sectionId,
            status: "error" as const,
            message: `Word refused the answer as a mytender.io revision (${describeError(authoredError)})`,
          };
        // Plain content may take the HTML route.
        let holder: Word.Paragraph;
        if (omitHeading && anchor.kind === "afterParagraph") {
          holder = anchor.paragraph.insertParagraph("", "After");
        } else {
          const heading = insertHeadingParagraphAt(context, anchor, titleOf(q).replace(" · ", " "));
          heading.styleBuiltIn = (["Heading1", "Heading2", "Heading3"] as const)[q.level - 1];
          holder = heading.insertParagraph("", "After");
        }
        holder.styleBuiltIn = "Normal";
        const range = holder.getRange("Whole");
        range.insertHtml(blocksHtml(blocks), "Replace");
        const cc = holder.getRange("Whole").insertContentControl();
        cc.tag = tag;
        cc.title = titleOf(q);
        cc.appearance = "Hidden";
        await context.sync();
        return { sectionId: q.sectionId, status: "added" as const };
      }
    });
  } catch (error) {
    return { sectionId: q.sectionId, status: "error", message: error instanceof Error ? error.message : String(error) };
  }
};

/** The agent's decision on where one new answer goes (see the pull tool's schema). */
export type AnswerPlacement = {
  position: "auto" | "under_heading" | "after_paragraph" | "start" | "end" | "before_closing_pages";
  index?: number;
  expected_text?: string;
  include_heading?: boolean;
};

/** The agent's choice of styles for pulled answers, by name as the document context lists them. */
export type PullStyleChoice = Partial<
  Record<"body" | "bullet" | "number" | "heading_1" | "heading_2" | "heading_3" | "table", string>
>;

export type PullItem = { q: QuestionRef; markdown: string; mode: PullMode; placement?: AnswerPlacement };

/**
 * The style map pulled answers are written in: the agent's choices resolved
 * against the applied template's styles (by name or id), or, for a style
 * the template does not list, Word's own id convention (the name with
 * spaces and punctuation removed, "ARO Response" -> "AROResponse").
 */
export const resolvePullStyles = (
  choice: PullStyleChoice | null | undefined,
  applied: TemplateStyleMap | null | undefined
): TemplateStyleMap | null | undefined => {
  if (!choice || !Object.values(choice).some(Boolean)) return undefined;
  const known: (TemplateStyleRef & { has_numbering?: boolean })[] = [
    ...(applied ? [applied.body] : []),
    ...(applied?.bullet ? [applied.bullet] : []),
    ...(applied?.number ? [applied.number] : []),
    ...(applied ? Object.values(applied.headings) : []),
    ...(applied?.table ? [applied.table] : []),
  ];
  const resolve = (name: string | undefined): (TemplateStyleRef & { has_numbering?: boolean }) | null => {
    if (!name?.trim()) return null;
    const key = name.trim().toLowerCase();
    const hit = known.find((s) => s.name.toLowerCase() === key || s.id.toLowerCase() === key);
    return hit ?? { id: name.trim().replace(/[^A-Za-z0-9]+/g, ""), name: name.trim() };
  };
  const body = resolve(choice.body) ?? applied?.body ?? { id: "Normal", name: "Normal" };
  const list = (name: string | undefined, fallback: (TemplateStyleRef & { has_numbering: boolean }) | null) => {
    const r = resolve(name);
    return r ? { ...r, has_numbering: r.has_numbering ?? false } : fallback;
  };
  const headings: Record<string, TemplateStyleRef> = { ...(applied?.headings ?? {}) };
  for (const level of ["1", "2", "3"] as const) {
    const r = resolve(choice[`heading_${level}`]);
    if (r) headings[level] = r;
  }
  return {
    body,
    bullet: list(choice.bullet, applied?.bullet ?? null),
    number: list(choice.number, applied?.number ?? null),
    headings,
    table: resolve(choice.table) ?? applied?.table ?? null,
    response_start: applied?.response_start ?? null,
  };
};

/**
 * Pull several answers in a handful of round trips instead of five or so per
 * question: missing questions and empty answer spaces are written in one
 * batch (missing ones that share an anchor go in as one XML, in mytender's
 * order), then every replaced answer is diffed with its control lifted off,
 * in one write, and the controls go back on together. Word takes one batch
 * at a time, so fewer, bigger batches are what "parallel" means here. A
 * failure before anything is written retries one question at a time; a
 * failure after it puts the controls back and reports what is missing.
 * @param bidId - The bid.
 * @param items - What to write; `mode` as in {@link pullIntoDocument}.
 * @param order - Every question's section id in mytender's order, for placing.
 * @returns One outcome per item.
 */
export const pullManyIntoDocument = async (
  bidId: string,
  items: PullItem[],
  order: string[],
  directives: { styles?: TemplateStyleMap | null } = {}
): Promise<PullOutcome[]> => {
  if (!items.length) return [];
  const styles = directives.styles;
  const canTrack = documentCapabilities().tracked_changes;
  const neighboursOf = (sectionId: string): Neighbours => {
    const at = order.indexOf(sectionId);
    return { before: order.slice(0, Math.max(0, at)).reverse(), after: at < 0 ? [] : order.slice(at + 1) };
  };
  const oneByOne = async (some: PullItem[]) => {
    const out: PullOutcome[] = [];
    for (const i of some)
      out.push(
        await pullIntoDocument(bidId, i.q, i.markdown, neighboursOf(i.q.sectionId), i.mode, {
          placement: i.placement,
          styles,
        })
      );
    return out;
  };
  const thisBid = (controls: Word.ContentControlCollection): Map<string, Word.ContentControl> => {
    const map = new Map<string, Word.ContentControl>();
    for (const c of controls.items) {
      const parsed = parseTag(c.tag);
      // The first block of a question is its block, as everywhere else.
      if (parsed && parsed.bidId === bidId && !map.has(parsed.sectionId)) map.set(parsed.sectionId, c);
    }
    return map;
  };
  let phase: "reading" | "written" | "done" = "reading";
  const outcomes = new Map<string, PullOutcome>();
  const applied = await loadAppliedTemplate().catch(() => null);
  try {
    await Word.run(async (context) => {
      // 1. Every block in the document; which items replace, which add.
      const controls = context.document.body.contentControls;
      controls.load("items/tag");
      await context.sync();
      const blockById = thisBid(controls);
      const replacing = items
        .filter((i) => blockById.has(i.q.sectionId))
        .map((item) => {
          const control = blockById.get(item.q.sectionId) as Word.ContentControl;
          const paragraphs = control.paragraphs;
          paragraphs.load("items/text,items/tableNestingLevel");
          const tables = control.tables;
          tables.load("items");
          return { item, control, paragraphs, tables, blocks: parseMarkdown(item.markdown) };
        });
      const adding = items.filter((i) => !blockById.has(i.q.sectionId));
      // Answers with no neighbour go by the document's landmarks: under a
      // heading the template already has for them, after its "response
      // starts here" heading, or before its closing pages; never after them.
      const landmarks = adding.length ? await loadLandmarks(context, applied?.styleMap?.response_start ?? null) : null;
      type Placement = {
        key: string;
        prev: Word.ContentControl | null;
        next: Word.ContentControl | null;
        landmark: { anchor: Anchor; omitHeading: boolean } | null;
      };
      const placements = adding.map((item): Placement => {
        // The agent's placement first; a stale one (the paragraph moved) or
        // none falls back to neighbours, then to the document's landmarks.
        const asked =
          landmarks && item.placement && item.placement.position !== "auto"
            ? placementAnchor(landmarks, item.placement)
            : null;
        if (asked) return { key: asked.key, prev: null, next: null, landmark: asked };
        const n = neighboursOf(item.q.sectionId);
        const prevId = n.before.find((id) => blockById.has(id));
        const nextId = prevId ? undefined : n.after.find((id) => blockById.has(id));
        const landmark = !prevId && !nextId && landmarks ? landmarkAnchor(landmarks, titleOf(item.q)) : null;
        return {
          key: prevId ? `after:${prevId}` : nextId ? `before:${nextId}` : (landmark?.key ?? "end"),
          prev: prevId ? (blockById.get(prevId) as Word.ContentControl) : null,
          next: nextId ? (blockById.get(nextId) as Word.ContentControl) : null,
          landmark: landmark ? { anchor: landmark.anchor, omitHeading: landmark.omitHeading } : null,
        };
      });
      const aboveNext = new Map<string, Word.Paragraph>();
      for (const p of placements) {
        if (p.next && !aboveNext.has(p.key)) {
          const above = p.next.paragraphs.getFirst().getPreviousOrNullObject();
          above.load("isNullObject,style,styleBuiltIn");
          aboveNext.set(p.key, above);
        }
      }
      await context.sync();

      const restore = canTrack ? await setTracking(context, Word.ChangeTrackingMode.off) : async () => undefined;
      let wroteTracked = false;
      try {
        // 2. Missing questions and empty answer spaces, in one write.
        const toDiff: typeof replacing = [];
        for (const r of replacing) {
          const hasText =
            r.tables.items.length > 0 ||
            r.paragraphs.items.some((p) => (p.tableNestingLevel ?? 0) === 0 && (p.text ?? "").trim());
          const id = r.item.q.sectionId;
          if (!r.blocks.length && !hasText) outcomes.set(id, { sectionId: id, status: "unchanged" });
          else if (r.item.mode === "tracked" || hasText) toDiff.push(r);
          else {
            r.control.insertOoxml(packageOf(blocksXml(r.blocks, { tracked: false, styles })), "End");
            for (const p of r.paragraphs.items) if (!(p.text ?? "").trim()) p.delete();
            outcomes.set(id, { sectionId: id, status: "replaced" });
          }
        }
        const groups = new Map<string, { anchor: Anchor; xml: string[] }>();
        adding.forEach((item, i) => {
          const p = placements[i];
          const tracked = item.mode === "tracked";
          if (tracked) wroteTracked = true;
          const xml =
            (p.landmark?.omitHeading
              ? ""
              : headingXml(titleOf(item.q).replace(" · ", " "), item.q.level, { tracked, styles })) +
            blockControlXml(
              blocksXml(parseMarkdown(item.markdown), { tracked, styles }),
              blockTag(bidId, item.q.sectionId),
              titleOf(item.q)
            );
          let group = groups.get(p.key);
          if (!group) {
            const above = aboveNext.get(p.key);
            const anchor: Anchor = p.prev
              ? { kind: "after", control: p.prev }
              : p.next
                ? above && !above.isNullObject && isHeadingStyle(above)
                  ? { kind: "beforeParagraph", paragraph: above }
                  : { kind: "before", control: p.next }
                : (p.landmark?.anchor ?? { kind: "end" });
            group = { anchor, xml: [] };
            groups.set(p.key, group);
          }
          group.xml.push(xml);
        });
        for (const g of groups.values()) insertXmlAt(context, g.anchor, g.xml.join(""));
        await context.sync();
        phase = "written";

        // 3. Replaced answers as diffs, queued together and synced once.
        if (toDiff.length) {
          const olds: { r: (typeof toDiff)[number]; old: OldContent }[] = [];
          for (const r of toDiff) {
            const old = await readOldContent(context, r.control);
            if (diffChanges(old, r.blocks)) olds.push({ r, old });
            else outcomes.set(r.item.q.sectionId, { sectionId: r.item.q.sectionId, status: "unchanged" });
          }
          const oldTables: Word.Table[] = [];
          for (const o of olds) {
            oldTables.push(...queueDiff(o.r.control, o.old, o.r.blocks).oldTables);
            wroteTracked = true;
          }
          await context.sync();
          if (oldTables.length) {
            const restoreTracked = await setTracking(context, Word.ChangeTrackingMode.trackAll);
            for (const t of oldTables) t.delete();
            await context.sync();
            await restoreTracked();
          }
          for (const o of olds)
            outcomes.set(o.r.item.q.sectionId, { sectionId: o.r.item.q.sectionId, status: "replaced" });
        }
        phase = "done";
      } finally {
        await restore();
      }
      if (wroteTracked) markAuthoredRevisionsWorking();
    });
  } catch (error) {
    console.warn(`Batched pull failed while ${phase}; pulling one question at a time`, error);
    if (phase === "reading") return oneByOne(items);
  }
  // 4. Every block still there? What isn't is pulled again one at a time.
  const present = (await listBlocks(bidId).catch(() => null))?.blocks ?? new Map<string, unknown>();
  const missing = items.filter((i) => !present.has(i.q.sectionId));
  const results: PullOutcome[] = items
    .filter((i) => present.has(i.q.sectionId))
    .map(
      (i) =>
        outcomes.get(i.q.sectionId) ?? {
          sectionId: i.q.sectionId,
          status: phase === "done" ? ("added" as const) : ("replaced" as const),
          message: phase === "written" ? "old text may remain" : undefined,
        }
    );
  return [...results, ...(await oneByOne(missing))];
};

/** Wrap paragraph spans in question blocks (first-time mapping). Text is untouched. */
export const wrapSpans = async (
  bidId: string,
  spans: { sectionId: string; start: number; end: number; title: string }[]
): Promise<number> =>
  Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items");
    await context.sync();
    let wrapped = 0;
    // Last span first, so earlier indexes stay valid.
    for (const span of [...spans].sort((a, b) => b.start - a.start)) {
      const first = paragraphs.items[span.start];
      const last = paragraphs.items[Math.min(span.end, paragraphs.items.length - 1)];
      if (!first || !last) continue;
      const cc = first.getRange("Whole").expandTo(last.getRange("Whole")).insertContentControl();
      cc.tag = blockTag(bidId, span.sectionId);
      cc.title = span.title;
      cc.appearance = "Hidden";
      wrapped += 1;
    }
    await context.sync();
    return wrapped;
  });

/** The document's paragraphs for mapping: index, first words, heading level. */
export const documentOutlineForMapping = async (): Promise<{ index: number; text: string; level: number | null }[]> =>
  Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text,items/style,items/styleBuiltIn,items/tableNestingLevel");
    await context.sync();
    return paragraphs.items
      .map((p, index) => {
        const h = HEADING.exec(String(p.styleBuiltIn ?? "")) ?? HEADING.exec(p.style ?? "");
        const text = (p.text ?? "").trim();
        return {
          index,
          text: text.slice(0, 160),
          level: h ? Number(h[1]) : null,
          inTable: (p.tableNestingLevel ?? 0) > 0,
        };
      })
      .filter((p) => p.text && !p.inTable)
      .map(({ index, text, level }) => ({ index, text, level }));
  });

export type AssignOutcome = { status: "moved" | "taken" | "wrapped" | "error"; message?: string };

/**
 * Make the selected text part of a question's answer. If the question has a
 * block, the selection is moved to the end of it (mytender.io revisions the
 * writer can reject). If not, the selection becomes its block in place, unless
 * it overlaps another question's answer: blocks can't nest, so the text is
 * taken over instead, moved into a new block straight after that answer.
 * Paragraphs already in this question's answer stay where they are.
 */
export const assignSelection = async (bidId: string, q: QuestionRef): Promise<AssignOutcome> => {
  try {
    return await Word.run(async (context) => {
      const selected = context.document.getSelection().paragraphs;
      selected.load("items/text,items/style");
      const control = controlFor(context, bidId, q.sectionId);
      control.load("isNullObject");
      await context.sync();
      if (!selected.items.length) return { status: "error" as const, message: "Select some text first." };

      const parents = selected.items.map((p) => {
        const c = p.parentContentControlOrNullObject;
        c.load("isNullObject,tag");
        return c;
      });
      await context.sync();
      const owner = (i: number) => (parents[i].isNullObject ? null : parseTag(parents[i].tag));
      const inThis = (i: number) => owner(i)?.bidId === bidId && owner(i)?.sectionId === q.sectionId;
      const moving = selected.items.filter((_, i) => !inThis(i));
      if (!moving.length) return { status: "error" as const, message: "That text is already part of this answer." };
      const firstMoving = selected.items.indexOf(moving[0]);
      if (selected.items.slice(firstMoving, firstMoving + moving.length).some((_, k) => inThis(firstMoving + k)))
        return { status: "error" as const, message: "Select text on one side of this answer only." };
      const others = parents.filter((c, i) => !inThis(i) && owner(i) !== null);

      const whole = moving[0].getRange("Whole").expandTo(moving[moving.length - 1].getRange("Whole"));

      if (control.isNullObject && !others.length) {
        const cc = whole.insertContentControl();
        cc.tag = blockTag(bidId, q.sectionId);
        cc.title = titleOf(q);
        cc.appearance = "Hidden";
        await context.sync();
        return { status: "wrapped" as const };
      }

      // Moved into this question's block, or into a new one after the last
      // answer the selection touched.
      const lastOther = others[others.length - 1];
      const { blocks } = await readRangeBlocks(context, whole);
      const canTrack = documentCapabilities().tracked_changes;
      const restore = canTrack ? await setTracking(context, Word.ChangeTrackingMode.off) : async () => undefined;
      try {
        if (!control.isNullObject) control.insertOoxml(packageOf(blocksXml(blocks)), "End");
        else
          insertXmlAt(
            context,
            { kind: "after", control: lastOther },
            blockControlXml(blocksXml(blocks), blockTag(bidId, q.sectionId), titleOf(q))
          );
        for (const p of moving) {
          if ((p.text ?? "").trim()) deleteParagraphAsAuthoredRevision(p, p.text ?? "", p.style ?? null);
        }
        await context.sync();
        await restore();
      } catch (authoredError) {
        await restore();
        // The move is a mytender.io revision or nothing.
        return {
          status: "error" as const,
          message: `Word refused the move as a mytender.io revision (${describeError(authoredError)}). Nothing was changed.`,
        };
      }
      return { status: control.isNullObject ? ("taken" as const) : ("moved" as const) };
    });
  } catch (error) {
    return { status: "error", message: error instanceof Error ? error.message : String(error) };
  }
};

/** Remove every question block of another bid (text stays), after relinking. */
export const releaseForeignBlocks = async (bidId: string): Promise<number> =>
  Word.run(async (context) => {
    const controls = context.document.body.contentControls;
    controls.load("items/tag");
    await context.sync();
    let released = 0;
    for (const c of controls.items) {
      const parsed = parseTag(c.tag);
      if (parsed && parsed.bidId !== bidId) {
        c.delete(true);
        released += 1;
      }
    }
    await context.sync();
    return released;
  });
